import { CommandType, Injector } from '@univerjs/core';
import { FUniver } from '@univerjs/core/facade';
import { IAccessor } from '@wendellhu/redi';
import { cellContent, clearComponentStyling, deleteComponent } from './utils';
import { eventBus } from './event-bus';
import { applyComponent, isRowPlacementAllowed, isValidPlacement } from './component-rules';
import type { ComponentType } from './component-rules';

async function forEachActiveRange(
  accessor: IAccessor,
  fn: (univerAPI: any, fWorksheet: any, fRange: any) => Promise<void> | void,
): Promise<boolean> {
  const univerAPI: any = FUniver.newAPI(accessor.get(Injector));
  const fWorkbook = univerAPI.getActiveWorkbook();
  if (!fWorkbook) return false;
  const fWorksheet = fWorkbook.getActiveSheet();
  if (!fWorksheet) return false;
  const fSelection = fWorksheet.getSelection();
  if (!fSelection) return false;

  for (const fRange of fSelection.getActiveRangeList()) {
    await fn(univerAPI, fWorksheet, fRange);
  }
  return true;
}

// Scan the whole selection before touching any of it. Partial application is not available here:
// deleteComponent goes through clear-selection-content, which the editor intercepts and re-runs
// over the entire selection, so a sub-range delete would still wipe the cells just refused.
async function refuseOrApply(
  accessor: IAccessor,
  refusedEvent: string,
  isAllowed: (fWorksheet: any, row: number, col: number) => boolean,
  apply: (univerAPI: any, fWorksheet: any, fRange: any) => Promise<void>,
): Promise<boolean> {
  const refused: Array<{ row: number; col: number }> = [];
  await forEachActiveRange(accessor, (_api, fWorksheet, fRange) => {
    const range = fRange.getRange();
    for (let row = range.startRow; row <= range.endRow; row++) {
      for (let col = range.startColumn; col <= range.endColumn; col++) {
        if (isAllowed(fWorksheet, row, col)) continue;
        refused.push({ row, col });
      }
    }
  });
  if (refused.length) {
    eventBus.emit(refusedEvent, { ...refused[0], skipped: refused.length });
    return true;
  }
  return forEachActiveRange(accessor, apply);
}

function makeInsertOperation(id: string, component: ComponentType, preserveValue = false) {
  return {
    id,
    type: CommandType.OPERATION,
    handler: (accessor: IAccessor) =>
      forEachActiveRange(accessor, async (univerAPI, _ws, fRange) => {
        await (preserveValue ? clearComponentStyling(fRange) : deleteComponent(fRange));
        applyComponent(univerAPI, fRange, component, preserveValue);
      }),
  };
}

function makeInsertPerCellOperation(id: string, component: ComponentType, preserveValue = false) {
  return {
    id,
    type: CommandType.OPERATION,
    handler: (accessor: IAccessor) =>
      forEachActiveRange(accessor, async (univerAPI, fWorksheet, fRange) => {
        const range = fRange.getRange();
        for (let r = range.startRow; r <= range.endRow; r++) {
          for (let c = range.startColumn; c <= range.endColumn; c++) {
            const cell = fWorksheet.getRange(r, c);
            await (preserveValue ? clearComponentStyling(cell) : deleteComponent(cell));
            applyComponent(univerAPI, cell, component, preserveValue);
          }
        }
      }),
  };
}

function makeInsertMergedOperation(id: string, component: ComponentType) {
  return {
    id,
    type: CommandType.OPERATION,
    handler: (accessor: IAccessor) =>
      forEachActiveRange(accessor, async (univerAPI, _ws, fRange) => {
        await deleteComponent(fRange);
        fRange.mergeAcross();
        applyComponent(univerAPI, fRange, component);
      }),
  };
}

export const InsertLabelOperation = makeInsertOperation('components-menu.operation.insert-label', 'LABEL', true);
export const InsertTextOperation = makeInsertOperation('components-menu.operation.insert-text', 'TEXT', true);
export const InsertFormulaOperation = {
  id: 'components-menu.operation.insert-formula',
  type: CommandType.OPERATION,
  handler: (accessor: IAccessor) => refuseOrApply(
    accessor,
    'formula-refused',
    (fWorksheet, row, col) => isRowPlacementAllowed(fWorksheet, row, col, 'FORMULA'),
    async (univerAPI, _ws, fRange) => {
      await deleteComponent(fRange);
      applyComponent(univerAPI, fRange, 'FORMULA');
    },
  ),
};
export const InsertNumberOperation = makeInsertOperation('components-menu.operation.insert-number', 'NUMBER');
export const InsertDateTimeOperation = makeInsertOperation('components-menu.operation.insert-datetime', 'DATETIME');
export const InsertValueOperation = {
  id: 'components-menu.operation.insert-value',
  type: CommandType.OPERATION,
  handler: (accessor: IAccessor) => refuseOrApply(
    accessor,
    'value-refused',
    (_ws, _row, col) => isValidPlacement('VALUE', col),
    async (univerAPI, fWorksheet, fRange) => {
      const r = fRange.getRange();
      // Captured before deleteComponent: applying VALUE wipes any existing link, and Cancel on
      // the confirmation bar has to be able to put it back.
      const armable: Array<{ row: number; col: number; previous: any }> = [];
      for (let row = r.startRow; row <= r.endRow; row++) {
        for (let col = r.startColumn; col <= r.endColumn; col++) {
          armable.push({ row, col, previous: cellContent(fWorksheet, row, col) });
        }
      }
      await deleteComponent(fRange);
      applyComponent(univerAPI, fRange, 'VALUE');
      const cells = armable.filter(
        ({ row, col }) => fWorksheet.getRange(row, col).getCustomMetaData()?.component === 'VALUE',
      );
      if (cells.length) {
        eventBus.emit('arm-value-cell', { sheetId: fWorksheet.getSheetId(), cells });
      }
    },
  ),
};
export const InsertCompanyOperation = makeInsertOperation('components-menu.operation.insert-company', 'COMPANY');
export const InsertCountryOperation = makeInsertOperation('components-menu.operation.insert-country', 'COUNTRY');
export const InsertCurrencyOperation = makeInsertOperation('components-menu.operation.insert-currency', 'CURRENCY');
export const InsertCompanyDropdownOperation = makeInsertOperation('components-menu.operation.insert-companydropdown', 'COMPANYDROPDOWN');

export const InsertLongLabelOperation = makeInsertMergedOperation('components-menu.operation.insert-longlabel', 'LONGLABEL');
export const InsertLongTextOperation = makeInsertMergedOperation('components-menu.operation.insert-longtext', 'LONGTEXT');

export const InsertHeaderOperation = makeInsertPerCellOperation('components-menu.operation.insert-header', 'HEADER', true);
export const InsertHeaderTextOperation = makeInsertPerCellOperation('components-menu.operation.insert-headertext', 'HEADERTEXT');

export const DeleteComponentOperation = {
  id: 'components-menu.operation.delete',
  type: CommandType.OPERATION,
  handler: (accessor: IAccessor) =>
    forEachActiveRange(accessor, async (_api, _ws, fRange) => {
      await deleteComponent(fRange);
    }),
};

export const InsertAccountCodeOperation = {
  id: 'components-menu.operation.insert-account-code',
  type: CommandType.OPERATION,
  handler: async () => {
    eventBus.emit('open-account-dialog');
    return true;
  },
};

// Univer's merge dropdown is hidden; only its per-row variant is storable.
export const MERGE_HORIZONTAL_COMMAND = 'sheet.command.add-worksheet-merge-horizontal';

export const MergeOperation = {
  id: 'components-menu.operation.merge',
  type: CommandType.OPERATION,
  handler: (accessor: IAccessor) => {
    const univerAPI: any = FUniver.newAPI(accessor.get(Injector));
    return univerAPI.executeCommand(MERGE_HORIZONTAL_COMMAND);
  },
};


// ----- Go to link cell / go back to last sheet -----------------------------------------------
// Univer owns the keyboard over the grid, so these run as commands bound through its shortcut
// service (see components-menu.controller) rather than from a DOM keydown listener, which never
// sees the key. The same commands back the context-menu entries.

// Where each jump started, newest last. Module state: the editor holds one workbook at a time and
// clears the trail whenever it swaps one in.
const linkTrail: Array<{ sheetId: string; row: number; column: number }> = [];

export function clearLinkTrail(): void {
  linkTrail.length = 0;
}

// The first sheet reference in the cell: Sheet!B12, 'Sheet name'!$B$12, Sheet!B12:C20.
const SHEET_REFERENCE = /(?:'([^']+)'|([A-Za-z0-9_.]+))!\$?([A-Za-z]+)\$?(\d+)/;

function columnIndex(letters: string): number {
  let index = 0;
  for (const letter of letters.toUpperCase()) {
    index = index * 26 + (letter.charCodeAt(0) - 64);
  }
  return index - 1;
}

export const GoToLinkCellOperation = {
  id: 'components-menu.operation.go-to-link-cell',
  type: CommandType.OPERATION,
  handler: (accessor: IAccessor) => {
    const univerAPI: any = FUniver.newAPI(accessor.get(Injector));
    const fWorkbook = univerAPI.getActiveWorkbook();
    const fWorksheet = fWorkbook?.getActiveSheet();
    const fRange = fWorksheet?.getSelection()?.getActiveRange();
    if (!fWorkbook || !fWorksheet || !fRange) return false;

    // getFormula() is empty on a plain cell, and a VALUE cell carries its link as text.
    const match = SHEET_REFERENCE.exec(String(fRange.getFormula() || fRange.getValue() || ''));
    if (!match) {
      eventBus.emit('link-cell-none');
      return false;
    }
    const name = match[1] || match[2];
    const target = fWorkbook.getSheetByName(name);
    if (!target) {
      eventBus.emit('link-cell-missing', { name });
      return false;
    }

    const range = fRange.getRange();
    linkTrail.push({
      sheetId: fWorksheet.getSheetId(),
      row: range.startRow,
      column: range.startColumn,
    });
    fWorkbook.setActiveSheet(target);
    target.getRange(Number(match[4]) - 1, columnIndex(match[3])).activate();
    return true;
  },
};

export const GoBackLinkCellOperation = {
  id: 'components-menu.operation.go-back-link-cell',
  type: CommandType.OPERATION,
  handler: (accessor: IAccessor) => {
    const univerAPI: any = FUniver.newAPI(accessor.get(Injector));
    const fWorkbook = univerAPI.getActiveWorkbook();
    if (!fWorkbook) return false;

    const previous = linkTrail.pop();
    if (!previous) {
      eventBus.emit('link-cell-no-history');
      return false;
    }
    // The tab may have been deleted while away; the trail entry is spent either way.
    const target = fWorkbook.getSheetBySheetId(previous.sheetId);
    if (!target) return false;

    fWorkbook.setActiveSheet(target);
    target.getRange(previous.row, previous.column).activate();
    return true;
  },
};
